/**
 * Store-dispatching half of permission-lock handling. Kept separate from
 * permission-lock-detection.util.ts (which has no store dependency) so
 * api.service.ts can detect+auto-dispatch these errors without importing the
 * store — mirrors plan-lock.util.ts's file split for the same reason.
 *
 * Import this from React components/hooks and from call-site catch blocks
 * (not from api.service.ts).
 *
 * NOTE on which function to use: since permission-lock-detection.util.ts's
 * interceptor now AUTO-dispatches the dialog for any non-suppressed tagged
 * error (unlike plan-lock, which stays opt-in), most call sites should use
 * `isPermissionLockTagged` — a plain check, no dispatch — just to decide
 * whether to ALSO show their own inline error (skip it if the modal is
 * already covering this). Calling `showPermissionLockIfTagged` instead isn't
 * wrong (dispatching the same payload twice is harmless/idempotent), just
 * redundant, UNLESS the underlying request was itself marked
 * `suppressPermissionLock` (background/polling) — for those, the interceptor
 * deliberately did NOT auto-dispatch, so a call site that still wants a
 * modal for that specific case must dispatch explicitly.
 */

import { store } from '@/store';
import { hidePermissionLock, showPermissionLock } from '@/store/slices/permissionLockSlice';
import {
  ApiErrorBody,
  buildPermissionLockPayload,
  isPermissionLockError,
} from './permission-lock-detection.util';

/** Pure check, no dispatch — was this error tagged as a permission denial (regardless of whether the auto-modal fired for it)? */
export function isPermissionLockTagged(error: any): boolean {
  return !!error?.permissionLock;
}

/**
 * Call from an axios-based catch block, on an error that already passed
 * through the interceptor. Returns true if the lock dialog was shown
 * (caller should skip its own setError/Alert in that case). See this file's
 * header comment for when to prefer `isPermissionLockTagged` instead.
 */
export function showPermissionLockIfTagged(error: any): boolean {
  if (error?.permissionLock) {
    store.dispatch(showPermissionLock(error.permissionLock));
    return true;
  }
  return false;
}

/**
 * Call from a raw-fetch catch block (no axios interceptor tags these) with
 * the parsed response body's `error` field. Returns true if the lock dialog
 * was shown.
 */
export function showPermissionLockFromErrorBody(apiError?: ApiErrorBody): boolean {
  if (apiError && isPermissionLockError(apiError.code) && apiError.message) {
    store.dispatch(showPermissionLock(buildPermissionLockPayload(apiError.message)));
    return true;
  }
  return false;
}

export function closePermissionLock(): void {
  store.dispatch(hidePermissionLock());
}
